import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClipboardCheck, Loader2, CheckCircle2, Fuel, Gauge } from "lucide-react";

/**
 * Pickup & Return Checklists — 12 items each, fuel level (0-100), odometer,
 * notes. Saves to /api/rentals/:id/checklist for either side.
 *
 * Props:
 *   rentalId: string
 *   side: "pickup" | "return"
 *   existing: object (record returned by backend) or null
 *   onSaved: callback
 */

const CHECKLIST_KEYS = [
  "exterior_clean",
  "interior_clean",
  "no_damage_body",
  "no_damage_glass",
  "tires_ok",
  "spare_tire",
  "all_lights_ok",
  "wipers_ok",
  "documents_present",
  "key_count_ok",
  "manual_present",
  "registration_present",
];

export default function RentalChecklist({ rentalId, side, existing, onSaved }) {
  const { t } = useT();
  const [items, setItems] = useState(() => Object.fromEntries(CHECKLIST_KEYS.map((k) => [k, false])));
  const [fuel, setFuel] = useState(100);
  const [odo, setOdo] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existing) {
      const fromExisting = Object.fromEntries(CHECKLIST_KEYS.map((k) => [k, !!existing.items?.[k]]));
      setItems(fromExisting);
      setFuel(existing.fuel_level ?? 100);
      setOdo(existing.odometer ?? "");
      setNotes(existing.notes ?? "");
    } else {
      setItems(Object.fromEntries(CHECKLIST_KEYS.map((k) => [k, false])));
      setFuel(100);
      setOdo("");
      setNotes("");
    }
  }, [existing, rentalId, side]);

  const toggle = (k) => setItems((prev) => ({ ...prev, [k]: !prev[k] }));
  const allCount = CHECKLIST_KEYS.length;
  const checkedCount = CHECKLIST_KEYS.filter((k) => items[k]).length;
  const pct = Math.round((checkedCount / allCount) * 100);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/rentals/${rentalId}/checklist`, {
        side,
        items,
        fuel_level: Number(fuel),
        odometer: odo === "" ? null : Number(odo),
        notes,
      });
      toast.success(t("checklist.saved"));
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const isReturn = side === "return";
  const sideAccent = isReturn ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-orange-50 border-[#F5A623]/30 text-[#D97706]";

  return (
    <div className="border border-zinc-200 bg-white" data-testid={`checklist-${side}-${rentalId}`}>
      <div className={`px-4 py-3 border-b ${sideAccent} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4" />
          <div className="overline">
            {isReturn ? t("checklist.titleReturn") : t("checklist.titlePickup")}
          </div>
        </div>
        <div className="font-mono text-xs">
          {checkedCount}/{allCount} · {pct}%
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
          {CHECKLIST_KEYS.map((k) => (
            <label
              key={k}
              className="flex items-center gap-2 cursor-pointer hover:bg-zinc-50 px-2 py-1 -mx-2"
              data-testid={`checklist-${side}-${k}`}
            >
              <input
                type="checkbox"
                checked={!!items[k]}
                onChange={() => toggle(k)}
                className="w-4 h-4 accent-[#F5A623]"
              />
              <span className={`text-sm ${items[k] ? "text-zinc-900" : "text-zinc-600"}`}>
                {t(`checklist.items.${k}`)}
              </span>
              {items[k] && <CheckCircle2 className="w-3 h-3 text-[#F5A623] ml-auto" />}
            </label>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-zinc-200">
          <div className="space-y-1.5">
            <label className="overline flex items-center gap-1.5"><Fuel className="w-3 h-3" /> {t("checklist.fuelLevel")}</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={fuel}
                onChange={(e) => setFuel(Number(e.target.value))}
                className="flex-1 accent-[#F5A623]"
                data-testid={`checklist-${side}-fuel`}
              />
              <div className="font-mono text-sm font-semibold w-12 text-right">{fuel}%</div>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="overline flex items-center gap-1.5"><Gauge className="w-3 h-3" /> {t("checklist.odometer")}</label>
            <Input
              type="number"
              className="rounded-none"
              value={odo}
              onChange={(e) => setOdo(e.target.value)}
              placeholder="mi"
              data-testid={`checklist-${side}-odo`}
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <label className="overline">{t("common.notes")}</label>
            <Input
              className="rounded-none"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("checklist.notesPlaceholder")}
              data-testid={`checklist-${side}-notes`}
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-zinc-200">
          {existing?.completed_at ? (
            <div className="text-[10px] text-zinc-500 font-mono">
              {t("checklist.completedAt")}: {new Date(existing.completed_at).toLocaleString()}
            </div>
          ) : <div />}
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            className="rounded-none bg-[#F5A623] hover:bg-[#D97706] text-white"
            data-testid={`checklist-save-${side}`}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-2" />}
            {t("common.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
